import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Card, Col, Container, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import SampleManagerNavigation from '../components/samples/SampleManagerNavigation';
import { SampleManagerPageHeading } from '../components/samples/SampleManagerPageHeading';
import { SamplePanelCardHeader } from '../components/samples/SamplePanelCardHeader';
import CaseLocationTree from '../components/samples/CaseLocationTree';
import CaseSampleListPanel, {
  type CaseHiddenStatusRevealMode,
} from '../components/samples/CaseSampleListPanel';
import SampleManagerModals from '../components/samples/SampleManagerModals';
import CollapsibleHelpSection from '../components/common/CollapsibleHelpSection';
import SampleFilterPanel, {
  SAMPLE_CASE_DETAILED_FILTER_STORAGE_KEY,
} from '../components/samples/SampleFilterPanel';
import '../components/samples/sampleManagerList.css';
import '../components/samples/sampleManagerList.mobile.css';
import { useAuth, isAdminUser } from '../hooks/useAuth';
import { useSampleStorageSetupGate } from '../hooks/useSampleStorageSetupGate';
import {
  SAMPLE_STORAGE_SETUP_HREF,
  clearStorageSetupModalSession,
} from '../utils/sampleStorageSetupGate';
import { SAMPLE_STATUS } from '../constants/sample.constants';
import {
  buildSampleListApiParams,
  DEFAULT_SAMPLE_LIST_STATUS_FILTERS,
  hasNonDefaultSampleListFilters,
  statusToStatId,
} from '../utils/sampleListFilterParams';
import { sampleService, Sample, SampleListParams } from '../services/sample.service';
import { isSampleLocalMode } from '../services/systems/sample-local/sampleLocalMode';
import {
  type CaseLocationCell,
  buildSampleListParamsForCell,
  caseLocationCellKey,
  findFirstPopulatedCaseCell,
  resolveInitialCaseManagerCell,
  getCaseLocationSampleCount,
  hasLegacyCaseLocationSearchParams,
  isCellInLocationIndex,
  stripLegacyCaseLocationSearchParams,
} from '../utils/caseLocationUtils';
import {
  readSampleRouterState,
  resolveCaseManagerPendingCell,
  stripSampleRouterStateKeys,
} from '../utils/samplePageNavigation';
import { canManageSample, canManageStorageRoom } from '../utils/samplePermissions';
import { useMdUp } from '../hooks/useLaboSukeMdUp';
import { loadEditableSample } from '../hooks/useSampleEditLoader';
import {
  getAxiosOrErrorMessage,
  getErrorCode,
  translatePrefixedErrorMessage,
} from '../utils/error.utils';
import { useAdminStorageViewAll } from '../hooks/useAdminStorageViewAll';
import AdminStorageViewToggle from '../components/samples/AdminStorageViewToggle';
import SampleExternalShareModal from '../components/samples/SampleExternalShareModal';
import {
  caseCellToLocationShareTarget,
  type LocationShareTarget,
} from '../utils/locationShare';

function findRoomName(
  index: Awaited<ReturnType<typeof sampleService.getLocationIndex>>['data'] | undefined,
  storageRoomId: number | null
): string | null {
  if (storageRoomId == null || !index) return null;
  return index.storageRooms.find((r) => r.id === storageRoomId)?.name ?? null;
}

/** 非表示パネルのフォーカス漏れ防止（React の inert 型は環境差があるため props 化） */
function inertProps(active: boolean): { inert?: '' } {
  return active ? { inert: '' } : {};
}

export default function SampleCaseManager() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const isMdUp = useMdUp();
  const { showAdminViewToggle, adminViewAll, setAdminViewAll } = useAdminStorageViewAll(isAdmin);
  const storageSetupGate = useSampleStorageSetupGate(adminViewAll);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [filters, setFilters] = useState<SampleListParams>({
    ...DEFAULT_SAMPLE_LIST_STATUS_FILTERS,
  });
  const [selectedCell, setSelectedCell] = useState<CaseLocationCell | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [detailSampleId, setDetailSampleId] = useState<number | null>(null);
  const [createPrefill, setCreatePrefill] = useState<Partial<Sample> | null>(null);
  const [locationShareTarget, setLocationShareTarget] = useState<LocationShareTarget | null>(null);
  const [showLocationShareModal, setShowLocationShareModal] = useState(false);
  const pendingCellConsumedRef = useRef(false);
  const routerCaseCellStrippedRef = useRef(false);

  /** 旧 ?room=&drawer=&case= を URL から除去（数字を URL に載せない） */
  useEffect(() => {
    if (!hasLegacyCaseLocationSearchParams(searchParams)) return;
    setSearchParams(stripLegacyCaseLocationSearchParams(searchParams), { replace: true });
  }, [searchParams, setSearchParams]);

  const { data: indexResponse, isLoading: indexLoading, isError: indexError } = useQuery({
    queryKey: ['samples', 'location-index', adminViewAll],
    queryFn: () => sampleService.getLocationIndex({ adminViewAll: adminViewAll || undefined }),
  });

  const locationIndex = indexResponse?.data;

  const selectedCellAuthorized = useMemo(() => {
    if (!selectedCell || !locationIndex) return false;
    return isCellInLocationIndex(locationIndex, selectedCell);
  }, [selectedCell, locationIndex]);

  const filtersForApi = useMemo(() => buildSampleListApiParams(filters), [filters]);
  const filtersKey = useMemo(() => JSON.stringify(filtersForApi), [filtersForApi]);
  const hasActiveFilters = hasNonDefaultSampleListFilters(filters);

  const listParams = useMemo(() => {
    if (!selectedCell) return null;
    return {
      ...filtersForApi,
      ...buildSampleListParamsForCell(selectedCell, { adminViewAll: adminViewAll || undefined }),
    } as SampleListParams;
  }, [selectedCell, filtersForApi, adminViewAll]);

  const {
    data: samplesResponse,
    isLoading: samplesLoading,
    isError: samplesError,
  } = useQuery({
    queryKey: [
      'samples',
      'case-cell',
      selectedCell ? caseLocationCellKey(selectedCell) : 'none',
      filtersKey,
      adminViewAll,
    ],
    queryFn: () => sampleService.list(listParams as SampleListParams),
    enabled: !!selectedCell && selectedCellAuthorized && !!listParams,
  });

  const samples = samplesResponse?.data ?? [];
  /** 既定の「利用可能」時、ツリー件数との差（残なし＋廃棄）を示唆する */
  const availableOnly = statusToStatId(filters.status) === 'available';
  const hiddenOutOfStockCount = availableOnly
    ? (samplesResponse?.stats?.outOfStock ?? 0)
    : 0;
  const hiddenDisposedCount = availableOnly
    ? (samplesResponse?.stats?.disposed ?? 0)
    : 0;

  const invalidateAll = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['samples'] });
    await queryClient.invalidateQueries({ queryKey: ['samples', 'location-index'] });
    if (selectedCell) {
      await queryClient.invalidateQueries({
        queryKey: ['samples', 'case-cell', caseLocationCellKey(selectedCell)],
      });
    }
  }, [queryClient, selectedCell]);

  const handleSelectCell = useCallback((cell: CaseLocationCell) => {
    setSelectedCell(cell);
    setError('');
  }, []);

  /**
   * 一覧／保管室からの Router state 復元。
   * 無ければデスクトップのみ先頭ケースを初期選択（モバイルはツリー優先）。
   */
  useEffect(() => {
    if (!locationIndex || pendingCellConsumedRef.current) return;
    pendingCellConsumedRef.current = true;
    const pending = resolveCaseManagerPendingCell(location);
    const initial = resolveInitialCaseManagerCell(locationIndex, pending, {
      autoSelectFirst: isMdUp,
    });
    if (initial) setSelectedCell(initial);
    if (!routerCaseCellStrippedRef.current && readSampleRouterState(location).caseManagerPendingCell) {
      routerCaseCellStrippedRef.current = true;
      stripSampleRouterStateKeys(navigate, location, ['caseManagerPendingCell']);
    }
  }, [locationIndex, location, navigate, isMdUp]);

  /** モバイルでツリーのまま横向き等 md 以上になったとき、右ペイン空を避ける */
  useEffect(() => {
    if (!isMdUp || !locationIndex || selectedCell || !pendingCellConsumedRef.current) return;
    const first = findFirstPopulatedCaseCell(locationIndex);
    if (first) setSelectedCell(first);
  }, [isMdUp, locationIndex, selectedCell]);

  const createMutation = useMutation({
    mutationFn: (data: Partial<Sample>) =>
      sampleService.create(data, { adminViewAll: adminViewAll || undefined }),
    onSuccess: async () => {
      await invalidateAll();
      setShowCreateModal(false);
      setCreatePrefill(null);
      setError('');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Sample> }) =>
      sampleService.update(id, data, { adminViewAll: adminViewAll || undefined }),
    onSuccess: async (_data, variables) => {
      await invalidateAll();
      await queryClient.invalidateQueries({ queryKey: ['samples', 'detail', variables.id] });
      setShowEditModal(false);
      setSelectedSample(null);
    },
    onError: () => {
      // SampleFormModal 内で表示
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      sampleService.delete(id, { adminViewAll: adminViewAll || undefined }),
    onSuccess: async () => {
      await invalidateAll();
      setShowDeleteModal(false);
      setSelectedSample(null);
      setError('');
    },
    onError: (err: unknown) => {
      setError(
        translatePrefixedErrorMessage(
          getAxiosOrErrorMessage(err) || '',
          t,
          t('sample.delete_failed')
        )
      );
    },
  });

  const addUsageMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: {
        quantity: number;
        usageDate: string;
        purpose?: string;
        notes?: string;
        action?: string;
      };
    }) => sampleService.addUsageRecord(id, data, { adminViewAll: adminViewAll || undefined }),
    onSuccess: async (_data, variables) => {
      await invalidateAll();
      await queryClient.invalidateQueries({ queryKey: ['samples', 'detail', variables.id] });
      setShowUsageModal(false);
      setSelectedSample(null);
      setError('');
    },
    onError: (err: unknown) => {
      const code = getErrorCode(err);
      const flatMessage = getAxiosOrErrorMessage(err) || '';
      if (
        code === 'ECONNABORTED' ||
        flatMessage.includes('timeout') ||
        flatMessage.includes('api.error.request_timeout')
      ) {
        setError(t('api.error.request_timeout'));
        return;
      }
      setError(translatePrefixedErrorMessage(flatMessage, t, t('sample.add_usage_record_failed')));
    },
  });

  const handleCreateOpen = () => {
    if (!selectedCell) return;
    setCreatePrefill({
      storageRoomId: selectedCell.storageRoomId ?? undefined,
      drawerNumber: selectedCell.drawerNumber ?? undefined,
      caseName: selectedCell.caseName ?? undefined,
    });
    setShowCreateModal(true);
  };

  const handleEdit = async (sample: Sample) => {
    const result = await loadEditableSample({
      sampleId: sample.id,
      userId: user?.id,
      isAdmin,
      adminViewAll,
      t,
      knownSample: sample,
      logContext: 'case manager edit',
    });
    if (result.ok === false) {
      setError(result.error);
      return;
    }
    setSelectedSample(result.sample);
    setShowEditModal(true);
  };

  const handleDelete = (sample: Sample) => {
    if (!canManageSample(sample, user?.id, { isAdmin, adminViewAll })) {
      setError(t('sample.case_manager.read_only_other_user'));
      return;
    }
    setSelectedSample(sample);
    setShowDeleteModal(true);
  };

  const roomName = findRoomName(locationIndex, selectedCell?.storageRoomId ?? null);
  const indexSampleCount = useMemo(() => {
    if (!locationIndex || !selectedCell || !selectedCellAuthorized) return null;
    return getCaseLocationSampleCount(locationIndex, selectedCell);
  }, [locationIndex, selectedCell, selectedCellAuthorized]);

  const canShareRoom = useCallback(
    (storageRoomId: number) => {
      if (isSampleLocalMode()) return false;
      const room = locationIndex?.storageRooms.find((r) => r.id === storageRoomId);
      if (!room) return false;
      return canManageStorageRoom(
        { userId: room.ownerUserId },
        user?.id,
        { isAdmin, adminViewAll }
      );
    },
    [locationIndex, user?.id, isAdmin, adminViewAll]
  );

  const canShareSelectedCase =
    !!selectedCellAuthorized &&
    selectedCell?.storageRoomId != null &&
    canShareRoom(selectedCell.storageRoomId);

  const openLocationShare = (target: LocationShareTarget) => {
    setLocationShareTarget(target);
    setShowLocationShareModal(true);
  };

  const renderCaseHelp = () => (
    <CollapsibleHelpSection
      compact
      titleKey="sample.case_manager.help_title"
      descriptionKey="sample.case_manager.help_desc"
      stepsKey={[
        'sample.case_manager.help_step1',
        'sample.case_manager.help_step2',
        'sample.case_manager.help_step3',
      ]}
      storageKey="sample_case_manager_page"
      defaultExpanded={false}
    />
  );

  useEffect(() => {
    if (!storageSetupGate.shouldRedirect) return;
    clearStorageSetupModalSession();
  }, [storageSetupGate.shouldRedirect]);

  if (!storageSetupGate.isReady) {
    return (
      <Container fluid className="mt-2 px-3 px-md-4" id="sample-case-manager-page">
        <SampleManagerNavigation />
        <SampleManagerPageHeading
          shortLabel={t('sample.case_manager.nav_short', { defaultValue: 'Cases' })}
          fullLabel={t('sample.case_manager.nav')}
          srOnly
        />
        <div className="text-center py-5" role="status" aria-live="polite" aria-busy="true">
          <Spinner animation="border">
            <span className="visually-hidden">{t('common.loading')}</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (storageSetupGate.shouldRedirect) {
    return <Navigate to={SAMPLE_STORAGE_SETUP_HREF} replace />;
  }

  return (
    <Container fluid className="mt-2 px-3 px-md-4" id="sample-case-manager-page">
      <SampleManagerNavigation />

      {/* ナビ短名「ケース」と可視 h1 が縦を奪うため sr-only。ヘルプはツリー／パネル帯へ */}
      <SampleManagerPageHeading
        shortLabel={t('sample.case_manager.nav_short', { defaultValue: 'Cases' })}
        fullLabel={t('sample.case_manager.nav')}
        srOnly
      />
      <p className="visually-hidden">
        {t('sample.case_manager.page_role')}
      </p>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {(indexError || samplesError) && (
        <Alert variant="danger">{t('sample.load_failed')}</Alert>
      )}

      <Row className="g-2 g-md-3 align-items-start">
        <Col
          md={4}
          lg={4}
          className={selectedCell ? 'd-none d-md-block' : undefined}
          aria-hidden={selectedCell && !isMdUp ? true : undefined}
          {...inertProps(Boolean(selectedCell && !isMdUp))}
        >
          <Card className="shadow-sm sample-panel-card">
            <SamplePanelCardHeader
              icon="fa-sitemap"
              title={t('sample.case_manager.panel_tree', { defaultValue: 'Tree' })}
              trailing={
                <div className="d-flex align-items-center gap-1 flex-shrink-0">
                  {renderCaseHelp()}
                  {showAdminViewToggle ? (
                    <AdminStorageViewToggle
                      checked={adminViewAll}
                      onChange={setAdminViewAll}
                      inputId="case-manager-admin-view-all"
                      compact
                    />
                  ) : null}
                </div>
              }
            />
            <Card.Body
              className="p-2 case-manager-tree-body"
              aria-busy={indexLoading || undefined}
            >
              {indexLoading ? (
                <div className="text-center py-4" role="status">
                  <Spinner animation="border" size="sm" className="text-secondary">
                    <span className="visually-hidden">{t('common.loading')}</span>
                  </Spinner>
                </div>
              ) : locationIndex && (isMdUp || !selectedCell) ? (
                /* xs 詳細中はツリーをアンマウント（d-none+aria-hidden でも見出しが a11y に残るのを防ぐ） */
                <CaseLocationTree
                  index={locationIndex}
                  selected={selectedCell}
                  onSelect={handleSelectCell}
                  currentUserId={user?.id}
                  canShareRoom={canShareRoom}
                  onShareLocation={openLocationShare}
                />
              ) : null}
            </Card.Body>
          </Card>
        </Col>
        <Col
          md={8}
          lg={8}
          className={!selectedCell ? 'd-none d-md-block' : undefined}
          aria-hidden={!selectedCell && !isMdUp ? true : undefined}
          {...inertProps(Boolean(!selectedCell && !isMdUp))}
        >
          <Card className="shadow-sm sample-panel-card">
            <SamplePanelCardHeader
              className="d-none d-md-block"
              icon="fa-flask"
              title={t('sample.case_manager.panel_samples')}
            />
            <Card.Body className="py-2">
              <CaseSampleListPanel
                selected={selectedCellAuthorized ? selectedCell : null}
                roomName={roomName}
                samples={samples}
                isLoading={samplesLoading}
                indexSampleCount={indexSampleCount}
                hiddenOutOfStockCount={hiddenOutOfStockCount}
                hiddenDisposedCount={hiddenDisposedCount}
                onRevealHiddenStatus={(mode: CaseHiddenStatusRevealMode) => {
                  if (mode === 'all') {
                    setFilters((prev) => {
                      const next = { ...prev };
                      delete next.status;
                      return next;
                    });
                    return;
                  }
                  setFilters((prev) => ({
                    ...prev,
                    status:
                      mode === 'disposed'
                        ? SAMPLE_STATUS.DISPOSED
                        : SAMPLE_STATUS.OUT_OF_STOCK,
                  }));
                }}
                showRestoreAvailableFilter={!availableOnly}
                onRestoreAvailableFilter={() =>
                  setFilters((prev) => ({
                    ...prev,
                    ...DEFAULT_SAMPLE_LIST_STATUS_FILTERS,
                  }))
                }
                currentUserId={user?.id}
                isAdmin={isAdmin}
                adminViewAll={adminViewAll}
                onDetail={(sample) => {
                  setDetailSampleId(sample.id);
                  setShowDetailModal(true);
                }}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onAddUsage={(sample) => {
                  setSelectedSample(sample);
                  setShowUsageModal(true);
                }}
                onCreateSample={handleCreateOpen}
                onChangeCase={() => setSelectedCell(null)}
                hasActiveFilters={hasActiveFilters}
                onClearFilters={() => setFilters({ ...DEFAULT_SAMPLE_LIST_STATUS_FILTERS })}
                headerTools={<div className="d-md-none flex-shrink-0">{renderCaseHelp()}</div>}
                canShareLocation={canShareSelectedCase}
                onShareLocation={(scope) => {
                  if (!selectedCell?.storageRoomId) return;
                  if (scope === 'room') {
                    openLocationShare({
                      scope: 'room',
                      storageRoomId: selectedCell.storageRoomId,
                    });
                    return;
                  }
                  if (scope === 'drawer') {
                    openLocationShare({
                      scope: 'drawer',
                      storageRoomId: selectedCell.storageRoomId,
                      drawerNumber: selectedCell.drawerNumber,
                    });
                    return;
                  }
                  const target = caseCellToLocationShareTarget(selectedCell);
                  if (target) openLocationShare(target);
                }}
                filterSlot={
                  selectedCell ? (
                    <SampleFilterPanel
                      filters={filters}
                      onFilterChange={setFilters}
                      onClear={() => setFilters({ ...DEFAULT_SAMPLE_LIST_STATUS_FILTERS })}
                      isAdmin={isAdmin}
                      hideLocationFilters
                      adminStorageViewAll={adminViewAll}
                      detailedFilterStorageKey={SAMPLE_CASE_DETAILED_FILTER_STORAGE_KEY}
                      defaultDetailedFilterOpen={false}
                      barVariant="plain"
                      keywordAriaLabel={t('sample.case_manager.sample_search_aria', {
                        defaultValue: 'サンプル検索',
                      })}
                    />
                  ) : null
                }
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <SampleManagerModals
        showCreateModal={showCreateModal}
        onCloseCreate={() => {
          setShowCreateModal(false);
          setCreatePrefill(null);
        }}
        onCreate={async (data) => {
          await createMutation.mutateAsync(data);
        }}
        createLoading={createMutation.isPending}
        createInitialData={createPrefill}
        adminViewAll={adminViewAll}
        showEditModal={showEditModal}
        onCloseEdit={() => {
          setShowEditModal(false);
          setSelectedSample(null);
        }}
        onUpdate={async (data) => {
          if (!selectedSample) return;
          await updateMutation.mutateAsync({ id: selectedSample.id, data });
        }}
        editSample={selectedSample}
        updateLoading={updateMutation.isPending}
        showDeleteModal={showDeleteModal}
        onCloseDelete={() => {
          setShowDeleteModal(false);
          setSelectedSample(null);
          setError('');
        }}
        onConfirmDelete={async () => {
          if (selectedSample) {
            await deleteMutation.mutateAsync(selectedSample.id);
          }
        }}
        deleteSample={selectedSample}
        deleteLoading={deleteMutation.isPending}
        showDetailModal={showDetailModal}
        onCloseDetail={() => {
          setShowDetailModal(false);
          setDetailSampleId(null);
        }}
        detailSampleId={detailSampleId}
        onDetailEdit={(sample) => {
          setShowDetailModal(false);
          void handleEdit(sample);
        }}
        onDetailDelete={(sample) => {
          setShowDetailModal(false);
          handleDelete(sample);
        }}
        showUsageModal={showUsageModal}
        onCloseUsage={() => {
          setShowUsageModal(false);
          setSelectedSample(null);
        }}
        usageSample={selectedSample}
        onUsageSubmit={async (data) => {
          if (!selectedSample) return;
          await addUsageMutation.mutateAsync({
            id: selectedSample.id,
            data: {
              quantity: data.quantity,
              usageDate: data.usageDate,
              purpose: data.purpose,
              notes: data.notes,
              action: data.action,
            },
          });
        }}
        usageLoading={addUsageMutation.isPending}
        userId={user?.id}
        isAdmin={isAdmin}
        onPermissionError={setError}
      />

      <SampleExternalShareModal
        mode="location"
        show={showLocationShareModal}
        onHide={() => {
          setShowLocationShareModal(false);
          setLocationShareTarget(null);
        }}
        locationTarget={locationShareTarget}
        adminViewAll={adminViewAll}
      />
    </Container>
  );
}
